import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Stack,
  Avatar,
  Box,
  Typography,
} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import { useSearchUsers } from "src/api/projectFolders/projectFolderService";
import { useDebounce } from "src/helpers/helperFunctions";

export interface SearchUser {
  id: number;
  username: string;
  user_display_name?: string;
}

interface GroupMemberPickerProps {
  /** Dialog open flag — gates the user-search query so it only runs when shown. */
  open: boolean;
  /** User ids to hide from results (already members, or already staged). */
  excludeIds: number[];
  /** Action mode (default): invoked when the admin picks a user from the dropdown. */
  onAdd?: (user: SearchUser) => void;
  /**
   * Staged mode: when `value` is provided (not undefined) the picker holds
   * the selection and reports changes via `onChange` instead of firing
   * `onAdd`; the parent clears it by passing `value={null}`.
   */
  value?: SearchUser | null;
  onChange?: (user: SearchUser | null) => void;
  /** Disables the search while a live add is in flight. */
  isAdding?: boolean;
  label?: string;
  textFieldTestId?: string;
}

/**
 * Search-a-user control shared by the group dialogs and share dialogs. Two
 * modes: action-on-select (default — picking a user fires `onAdd` and clears
 * the field) and staged (`value` provided — the picker holds the selection
 * for a separate confirm control).
 */
const GroupMemberPicker: React.FC<GroupMemberPickerProps> = ({
  open,
  excludeIds,
  onAdd,
  value,
  onChange,
  isAdding = false,
  label = "Search users by name or email",
  textFieldTestId = "group-member-search",
}) => {
  const staged = value !== undefined;
  const [query, setQuery] = useState("");

  // 300ms debounce so each keystroke doesn't fire user-search.
  const debouncedQuery = useDebounce(query, 300);
  const { data: searchResults, isLoading: searchLoading } = useSearchUsers(
    debouncedQuery,
    open && debouncedQuery.length >= 2,
  );

  // Staged mode: a parent-driven clear (selected -> null) empties the input.
  useEffect(() => {
    if (staged && value === null) setQuery("");
  }, [staged, value]);

  const excluded = new Set(excludeIds);

  const handleSelect = (user: SearchUser | null) => {
    if (staged) {
      onChange?.(user);
      return;
    }
    if (!user) return;
    onAdd?.(user);
    // Clear so the field is ready for the next pick rather than showing the
    // chosen user's name.
    setQuery("");
  };

  return (
    <Autocomplete
      fullWidth
      options={searchResults || []}
      getOptionLabel={(option) => option.user_display_name || option.username}
      filterOptions={(options) => options.filter((o) => !excluded.has(o.id))}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={searchLoading}
      disabled={isAdding}
      // Action mode never holds a selection: a pick is an action, not a
      // sticky value. Staged mode holds the parent's.
      value={staged ? value : null}
      onChange={(_e, v) => handleSelect(v)}
      inputValue={query}
      onInputChange={(_e, v, reason) => {
        // Action mode ignores the post-select "reset" so it can't repopulate
        // the cleared field; staged mode wants the chosen label shown.
        if (staged || reason !== "reset") setQuery(v);
      }}
      blurOnSelect={!staged}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar sx={{ width: 32, height: 32 }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2">
                {option.user_display_name || option.username}
              </Typography>
              {option.user_display_name && (
                <Typography variant="caption" color="text.secondary">
                  {option.username}
                </Typography>
              )}
            </Box>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          data-testid={textFieldTestId}
        />
      )}
    />
  );
};

export default GroupMemberPicker;
